import { useEffect, useState } from 'react';
import { useApi } from '@/lib/api';
import { useMe } from '@/lib/useMe';
import { MeGate } from '@/components/MeGate';
import { CoreIcon } from '@/components/CoreIcon';
import { cn } from '@/lib/utils';

interface Overview {
  totals: {
    total: number;
    open: number;
    awaiting: number;
    resolved: number;
    spam: number;
    avg_confidence: number | null;
  };
  byCategory: Array<{ category: string; count: number }>;
  byPriority: Array<{ priority: string; count: number }>;
  toolStats: Array<{ tool_id: string; status: string; count: number }>;
  recentVolume: Array<{ day_epoch: number; count: number }>;
}

export function Reports() {
  const meStatus = useMe();
  return <MeGate status={meStatus}>{(me) => <ReportsBody orgId={me.org!.id} />}</MeGate>;
}

function ReportsBody({ orgId }: { orgId: string }) {
  const api = useApi();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get<Overview>(`/api/orgs/${orgId}/reports/overview`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api, orgId]);

  if (loading || !data) {
    return (
      <div className="h-full overflow-y-auto">
        <header className="p-6 border-b bg-background">
          <h1 className="text-xl font-semibold">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Agent performance and ticket analytics.</p>
        </header>
        <div className="p-6">
          <SkeletonGrid />
        </div>
      </div>
    );
  }

  const { totals, byCategory, byPriority, toolStats, recentVolume } = data;
  const resolutionRate = totals.total > 0
    ? Math.round((totals.resolved / totals.total) * 100)
    : 0;
  const autoRate = totals.total > 0
    ? Math.round(((totals.resolved) / Math.max(totals.total - totals.spam, 1)) * 100)
    : 0;

  const toolAgg = aggregateTools(toolStats);

  return (
    <div className="h-full overflow-y-auto">
      <header className="p-6 border-b bg-background">
        <h1 className="text-xl font-semibold">Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Agent performance and ticket analytics.</p>
      </header>

      <div className="p-6 max-w-6xl space-y-6">
        {/* ── KPI cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard icon="inbox" label="Total tickets" value={totals.total} />
          <KpiCard icon="check-alt" label="Resolution rate" value={`${resolutionRate}%`} accent={resolutionRate >= 80} />
          <KpiCard
            icon="speedometer"
            label="Avg confidence"
            value={totals.avg_confidence !== null ? `${Math.round(totals.avg_confidence * 100)}%` : '—'}
          />
          <KpiCard icon="bolt" label="Auto-resolved" value={`${autoRate}%`} />
        </div>

        {/* ── Two-column: Status breakdown + Category ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Status breakdown */}
          <div className="card p-5">
            <div className="mono-label mb-4 flex items-center gap-1.5">
              <CoreIcon name="chart-line" size={12} />
              Status breakdown
            </div>
            <div className="space-y-3">
              <BarRow label="Open" count={totals.open} total={totals.total} color="bg-accent" />
              <BarRow label="Needs review" count={totals.awaiting} total={totals.total} color="bg-warning" />
              <BarRow label="Resolved" count={totals.resolved} total={totals.total} color="bg-success" />
              <BarRow label="Spam" count={totals.spam} total={totals.total} color="bg-muted-foreground" />
            </div>
          </div>

          {/* Category distribution */}
          <div className="card p-5">
            <div className="mono-label mb-4 flex items-center gap-1.5">
              <CoreIcon name="tags" size={12} />
              By category
            </div>
            {byCategory.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categorized tickets yet.</p>
            ) : (
              <div className="space-y-3">
                {byCategory.map((c) => (
                  <BarRow key={c.category} label={c.category} count={c.count} total={totals.total} color="bg-foreground" />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Two-column: Priority + Tools ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Priority */}
          <div className="card p-5">
            <div className="mono-label mb-4 flex items-center gap-1.5">
              <CoreIcon name="fire" size={12} />
              By priority
            </div>
            {byPriority.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tickets yet.</p>
            ) : (
              <div className="space-y-3">
                {byPriority.map((p) => (
                  <BarRow
                    key={p.priority}
                    label={p.priority}
                    count={p.count}
                    total={totals.total}
                    color={
                      p.priority === 'urgent' ? 'bg-destructive'
                      : p.priority === 'high' ? 'bg-accent'
                      : p.priority === 'normal' ? 'bg-foreground'
                      : 'bg-muted-foreground'
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Tool usage */}
          <div className="card p-5">
            <div className="mono-label mb-4 flex items-center gap-1.5">
              <CoreIcon name="puzzle" size={12} />
              Tool usage
            </div>
            {toolAgg.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tool calls recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {toolAgg.map((t) => (
                  <div key={t.tool_id} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                    <span className="font-medium text-[13px] truncate flex-1 min-w-0">
                      {t.tool_id.replace(/^(composio\.|custom_http\.|builtin\.|mcp\.)/, '')}
                    </span>
                    <div className="flex items-center gap-3 text-[11px] shrink-0">
                      <span className="text-success tabular-nums">{t.success} ok</span>
                      {t.error > 0 && <span className="text-destructive tabular-nums">{t.error} err</span>}
                      <span className="text-muted-foreground tabular-nums">{t.total} total</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Volume chart (simple bar chart) ── */}
        <div className="card p-5">
          <div className="mono-label mb-4 flex items-center gap-1.5">
            <CoreIcon name="calendar" size={12} />
            Daily volume (last 30 days)
          </div>
          {recentVolume.length === 0 ? (
            <p className="text-sm text-muted-foreground">No ticket data yet.</p>
          ) : (
            <VolumeChart data={recentVolume} />
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, accent }: { icon: string; label: string; value: string | number; accent?: boolean }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-2">
        <CoreIcon name={icon} size={14} className="text-muted-foreground" />
        <span className="mono-label">{label}</span>
      </div>
      <div className={cn('text-2xl font-semibold tabular-nums tracking-sub', accent && 'text-success')}>
        {value}
      </div>
    </div>
  );
}

function BarRow({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[13px] mb-1">
        <span className="capitalize">{label}</span>
        <span className="text-muted-foreground tabular-nums">{count}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
    </div>
  );
}

function VolumeChart({ data }: { data: Array<{ day_epoch: number; count: number }> }) {
  const sorted = [...data].sort((a, b) => a.day_epoch - b.day_epoch);
  const max = Math.max(...sorted.map((d) => d.count), 1);

  return (
    <div className="flex items-end gap-1 h-32">
      {sorted.map((d) => {
        const pct = (d.count / max) * 100;
        const date = new Date(d.day_epoch * 86400000);
        const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return (
          <div key={d.day_epoch} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div
              className="w-full bg-foreground rounded-sm min-h-[2px] transition-all hover:bg-accent"
              style={{ height: `${Math.max(pct, 2)}%` }}
            />
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-surface text-[10px] px-1.5 py-0.5 rounded-sm whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
              {label}: {d.count}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function aggregateTools(stats: Array<{ tool_id: string; status: string; count: number }>) {
  const map = new Map<string, { tool_id: string; total: number; success: number; error: number }>();
  for (const s of stats) {
    const entry = map.get(s.tool_id) ?? { tool_id: s.tool_id, total: 0, success: 0, error: 0 };
    entry.total += s.count;
    if (s.status === 'success') entry.success += s.count;
    if (s.status === 'error') entry.error += s.count;
    map.set(s.tool_id, entry);
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

function SkeletonGrid() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="card p-4 animate-pulse">
            <div className="h-3 bg-muted rounded w-1/2 mb-3" />
            <div className="h-7 bg-muted rounded w-2/3" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="card p-5 animate-pulse">
            <div className="h-3 bg-muted rounded w-1/3 mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="h-4 bg-muted rounded" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
